/**
 * Vault switcher — a row of vault "pills" (each a link to `/vaults/:id`) plus
 * inline create, and rename/delete for the active vault. Names are decrypted in
 * the provider; this component is presentational over `useVaultData`.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVaultData } from '../store/vault-data';
import { Button, ErrorNote, Input } from './ui';

export function VaultSwitcher({ activeVaultId }: { activeVaultId: string }) {
  const { vaults, createVault, renameVault, deleteVault } = useVaultData();
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const active = vaults.find((v) => v.id === activeVaultId);
  const isOnlyVault = vaults.length <= 1;

  const run = (fn: () => Promise<void>): void => {
    setBusy(true);
    setError(null);
    void fn()
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {vaults.map((v) => {
          const isActive = v.id === activeVaultId;
          return (
            <Link
              key={v.id}
              to={`/vaults/${v.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition focus-visible:shadow-focus ${
                isActive ? 'bg-accent text-white' : 'bg-surface-alt text-text hover:bg-border'
              }`}
            >
              {v.name}
              <span
                className={`rounded-full px-1.5 text-xs ${
                  isActive ? 'bg-white/20' : 'bg-border text-text-secondary'
                }`}
              >
                {v.itemCount}
              </span>
            </Link>
          );
        })}

        {creating ? (
          <span className="inline-flex items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Vault name"
              className="h-8 w-40 py-1"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim().length > 0) {
                  run(async () => {
                    const id = await createVault(newName);
                    setNewName('');
                    setCreating(false);
                    navigate(`/vaults/${id}`);
                  });
                }
                if (e.key === 'Escape') {
                  setCreating(false);
                  setNewName('');
                }
              }}
            />
            <Button
              variant="ghost"
              disabled={busy || newName.trim().length === 0}
              onClick={() =>
                run(async () => {
                  const id = await createVault(newName);
                  setNewName('');
                  setCreating(false);
                  navigate(`/vaults/${id}`);
                })
              }
            >
              Add
            </Button>
            <Button variant="ghost" onClick={() => setCreating(false)} disabled={busy}>
              Cancel
            </Button>
          </span>
        ) : (
          <Button variant="ghost" onClick={() => setCreating(true)}>
            + New vault
          </Button>
        )}
      </div>

      {active !== undefined && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {renaming ? (
            <>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="h-8 w-48 py-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameValue.trim().length > 0) {
                    run(async () => {
                      await renameVault(active.id, renameValue);
                      setRenaming(false);
                    });
                  }
                  if (e.key === 'Escape') {
                    setRenaming(false);
                  }
                }}
              />
              <Button
                variant="ghost"
                disabled={busy || renameValue.trim().length === 0}
                onClick={() =>
                  run(async () => {
                    await renameVault(active.id, renameValue);
                    setRenaming(false);
                  })
                }
              >
                Save
              </Button>
              <Button variant="ghost" onClick={() => setRenaming(false)} disabled={busy}>
                Cancel
              </Button>
            </>
          ) : confirmDelete ? (
            <>
              <span className="text-text-secondary">
                Delete “{active.name}”
                {active.itemCount > 0 ? ` and its ${active.itemCount} item(s)` : ''}?
              </span>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await deleteVault(active.id);
                    setConfirmDelete(false);
                    // Navigate to another remaining vault after deleting the active one.
                    const next = vaults.find((v) => v.id !== active.id);
                    navigate(next !== undefined ? `/vaults/${next.id}` : '/vaults');
                  })
                }
              >
                {busy ? 'Deleting…' : 'Delete vault'}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="text-text-secondary underline-offset-2 hover:text-text hover:underline"
                onClick={() => {
                  setRenameValue(active.name);
                  setRenaming(true);
                }}
              >
                Rename
              </button>
              {!isOnlyVault && (
                <button
                  type="button"
                  className="text-text-secondary underline-offset-2 hover:text-error hover:underline"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete vault
                </button>
              )}
            </>
          )}
        </div>
      )}

      {error !== null && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}
